interface EncodedRequestOptions {
  publicKey: Omit<PublicKeyCredentialRequestOptions, "challenge" | "allowCredentials"> & {
    challenge: string;
    allowCredentials?: EncodedDescriptor[];
  };
}

interface EncodedCreationOptions {
  publicKey: Omit<PublicKeyCredentialCreationOptions, "challenge" | "user" | "excludeCredentials"> & {
    challenge: string;
    user: Omit<PublicKeyCredentialUserEntity, "id"> & { id: string };
    excludeCredentials?: EncodedDescriptor[];
  };
}

type EncodedDescriptor = Omit<PublicKeyCredentialDescriptor, "id"> & { id: string };

export const toRequestOptions = (oryOptions: string): CredentialRequestOptions => {
  const { publicKey } = JSON.parse(oryOptions) as EncodedRequestOptions;
  return {
    publicKey: {
      ...publicKey,
      challenge: fromBase64Url(publicKey.challenge),
      allowCredentials: publicKey.allowCredentials?.map(decodeDescriptor),
    },
  };
};

export const toCreationOptions = (oryOptions: string): CredentialCreationOptions => {
  const { publicKey } = JSON.parse(oryOptions) as EncodedCreationOptions;
  return {
    publicKey: {
      ...publicKey,
      challenge: fromBase64Url(publicKey.challenge),
      user: { ...publicKey.user, id: fromBase64Url(publicKey.user.id) },
      excludeCredentials: publicKey.excludeCredentials?.map(decodeDescriptor),
    },
  };
};

export const assertionForOry = (credential: PublicKeyCredential): string => {
  const response = credential.response as AuthenticatorAssertionResponse;
  return JSON.stringify({
    id: credential.id,
    rawId: toBase64Url(credential.rawId),
    type: credential.type,
    response: {
      authenticatorData: toBase64Url(response.authenticatorData),
      clientDataJSON: toBase64Url(response.clientDataJSON),
      signature: toBase64Url(response.signature),
      userHandle: response.userHandle ? toBase64Url(response.userHandle) : undefined,
    },
  });
};

export const attestationForOry = (credential: PublicKeyCredential): string => {
  const response = credential.response as AuthenticatorAttestationResponse;
  return JSON.stringify({
    id: credential.id,
    rawId: toBase64Url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: toBase64Url(response.attestationObject),
      clientDataJSON: toBase64Url(response.clientDataJSON),
    },
  });
};

export const isCancelledCeremony = (error: unknown): error is DOMException =>
  error instanceof DOMException && (error.name === "NotAllowedError" || error.name === "AbortError");

const decodeDescriptor = (descriptor: EncodedDescriptor): PublicKeyCredentialDescriptor => ({
  ...descriptor,
  id: fromBase64Url(descriptor.id),
});

export const fromBase64Url = (value: string): ArrayBuffer => {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "="));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0)).buffer;
};

export const toBase64Url = (value: ArrayBuffer): string =>
  btoa(String.fromCharCode(...new Uint8Array(value)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
